import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../../Common/DataTableHeader/DataTableHeader";
import { leaveRequestColumns } from "./LeaveRequestTableData";
import { leaveRequestApi } from "../../../../../../api/leaveRequestApi";
import { REACT_APP_API_URL } from "../../../../../../env";

const HotelStaffsLeaveView = () => {
    const [rows, setRows] = useState([]);
    const [exportRows, setExportRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [filterName, setFilterName] = useState("");
    const [filterMonth, setFilterMonth] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    const renderStatusBadge = (status) => {
        const normalized = String(status || "pending").toLowerCase();
        if (normalized === "approved")
            return <span className="badge bg-success">Approved</span>;
        if (normalized === "rejected")
            return <span className="badge bg-danger">Rejected</span>;
        return <span className="badge bg-warning text-dark">Pending</span>;
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await leaveRequestApi.getAllLeaveRequests();

                const items = Array.isArray(response?.data)
                    ? response.data
                    : [];

                /* FILTER → Only ApplyingTo = MANAGER */
                const filtered = items.filter(
                    (item) =>
                        String(item.applying_to || item.ApplyingTo)
                            .toUpperCase() === "MANAGER"
                );

                const uiRows = [];
                const exportData = [];

                filtered
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .forEach((item) => {
                        const requestId = item.id;
                        const appliedOnRaw = item.created_at;
                        const employeeName = item.employe_name || "Unknown";
                        const leaveType = item.leave_type || "-";
                        const fromDate = item.leave_from || "";
                        const toDate = item.leave_to || "";
                        const totalDays = item.total_days || 1;
                        const reason = item.reason || "";
                        const managerRemarks = item.manager_remarks || "-";
                        const statusRaw = item.status || "pending";
                        const attachmentPath = item.attachment || null;
                        const managerName = item.manager_name || item.ManagerName || "-";

                        uiRows.push({
                            __id: requestId,
                            employeeName: <span className="fw-semibold">{employeeName}</span>,
                            appliedOnRaw,
                            appliedOn: (
                                <span className="text-muted small">
                                    {appliedOnRaw ? formatDate(appliedOnRaw) : "-"}
                                </span>
                            ),
                            applyingTo: managerName,
                            leaveType: <span className="badge bg-primary">{leaveType}</span>,
                            from: fromDate,
                            to: toDate,
                            totalDays: <span className="badge bg-info">{totalDays}</span>,
                            reason,
                            managerRemarks: (
                                <span title={managerRemarks}>
                                    {managerRemarks.length > 30
                                        ? managerRemarks.slice(0, 30) + "..."
                                        : managerRemarks}
                                </span>
                            ),
                            status: renderStatusBadge(statusRaw),
                            attachment: attachmentPath ? (
                                <a
                                    href={`${REACT_APP_API_URL}${attachmentPath}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-secondary"
                                >
                                    View
                                </a>
                            ) : (
                                <span className="text-muted small">No File</span>
                            ),
                        });

                        exportData.push({
                            employeeName,
                            appliedOn: appliedOnRaw ? formatDate(appliedOnRaw) : "-",
                            leaveType,
                            from: fromDate,
                            to: toDate,
                            totalDays,
                            reason,
                            status: statusRaw,
                        });
                    });

                setRows(uiRows);
                setExportRows(exportData);

            } catch (err) {
                setError("Failed to load leave requests");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const tableData = useMemo(() => {
        return rows.filter((row) => {
            let match = true;

            if (filterName) {
                const name =
                    row.employeeName?.props?.children?.toLowerCase() || "";
                match = match && name.includes(filterName.toLowerCase());
            }

            if (filterMonth) {
                if (!row.appliedOnRaw) return false;
                const date = new Date(row.appliedOnRaw);
                const rowMonth = date.toISOString().slice(0, 7);
                match = match && rowMonth === filterMonth;
            }

            if (filterStatus) {
                const status =
                    row.status?.props?.children?.toLowerCase() || "";
                match = match && status === filterStatus.toLowerCase();
            }

            return match;
        });
    }, [rows, filterName, filterMonth, filterStatus]);

    return (

        <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="col-sm-12 mb-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <h3 className="fw-bold mb-0">Hotel Staff Leave Records</h3>
                </div>
            </div>

            <div className="col-sm-12">
                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive" style={{ maxHeight: "500px" }}>
                            <DataTable
                                columns={
                                    leaveRequestColumns.filter(
                                        (c) =>
                                            c.accessor !== "actions" &&
                                            c.accessor !== "print"
                                    )
                                }
                                data={tableData}
                                loading={loading}
                                reportMode={true}
                                disableSorting={true}
                                enablePagination={false}
                                onExportData={setExportRows}
                            />
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HotelStaffsLeaveView;